import { useEffect, type FC, type PropsWithChildren, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSessionStore } from '@/entities/session';
import { useTwoFactorModalAfterLogin } from '@/features/user-settings-2fa';
import {
  useAppSettingsStore,
  useSessionIdle,
  useWidgetMode,
} from '@/shared/lib';
import { useIdleTimer } from 'react-idle-timer';
import { usePhoneVerificationModalAfterLogin } from '@/features/user-settings-phone/model';

const ProtectedRoute: FC = ({
  redirectPath = '/login',
}: PropsWithChildren<{ redirectPath?: string }>) => {
  const { session, isSessionValid, logout, isLoggingOut } = useSessionStore();
  const { setPreviousNavigateUrl } = useAppSettingsStore();
  const isAuthenticated = isSessionValid();
  useTwoFactorModalAfterLogin(session);
  usePhoneVerificationModalAfterLogin(session);
  const { isWidget, handleTradingUrlNavigate } = useWidgetMode();
  const { logoutTime, handleUpdateReturnInfo } = useSessionIdle();

  const location = useLocation();

  const onIdle = useCallback(async () => {
    if (isLoggingOut) return;

    handleUpdateReturnInfo();
    await logout();
  }, [isLoggingOut, logout, handleUpdateReturnInfo]);

  useIdleTimer({
    onIdle,
    timeout: logoutTime,
    throttle: 500,
    disabled: isLoggingOut || import.meta.env.DEV,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setPreviousNavigateUrl(location.pathname + location.search);
    }
  }, []);

  useEffect(() => {
    if (isWidget && isAuthenticated) {
      handleTradingUrlNavigate();
    }
  }, [isWidget, isAuthenticated]);

  if (isWidget && isAuthenticated) {
    return <></>;
  }

  return isAuthenticated ? <Outlet /> : <Navigate to={redirectPath} />;
};

export default ProtectedRoute;
